import React, { useEffect, useRef, useState } from "react";
import styled, { css } from "styled-components";
import { Panel as BootstrapPanel } from "react-bootstrap";
import deprecationNotice from "util/deprecationNotice";
const PanelHeading = styled(BootstrapPanel.Heading)(
  ({ theme }) => css`
    .panel-title {
      > a {
        color: ${theme.colors.text.primary};
        display: block;
      }
    }
  `
);
const PanelFooter = styled(BootstrapPanel.Footer)(
  ({ theme }) => css`
    background-color: ${theme.colors.gray[90]};
    border-top-color: ${theme.colors.gray[80]};
  `
);
const panelVariantStyles = css(({ bsStyle = "default", theme }) => {
  const backgroundColor = theme.colors.alerts[bsStyle].background;
  const borderColor = theme.colors.alerts[bsStyle].border;
  return css`
    border-color: ${borderColor};

    > ${PanelHeading} {
      background-color: ${backgroundColor};
      border-color: ${borderColor};

      + .panel-collapse > .panel-body {
        border-top-color: ${borderColor};
      }
    }

    > ${PanelFooter} {
      + .panel-collapse > .panel-body {
        border-bottom-color: ${borderColor};
      }
    }
  `;
});
const StyledPanel = styled(BootstrapPanel)(
  ({ theme }) => css`
    background-color: transparent;

    .panel-heading,
    .panel-heading h3 {
      color: ${theme.colors.text.primary};
      font-size: ${theme.fonts.size.large};
    }

    .panel-body {
      background: ${theme.colors.global.contentBackground};
    }

    .panel-group {
      > ${PanelHeading} {
        + .panel-collapse > .panel-body,
        + .panel-collapse > .list-group {
          border-top-color: ${theme.colors.gray[90]};
        }
      }

      > ${PanelFooter} {
        + .panel-collapse .panel-body {
          border-bottom-color: ${theme.colors.gray[90]};
        }
      }
    }

    ${panelVariantStyles}
  `
);
const deprecatedVariantStyles = css(({ bsStyle = "default", theme }) => {
  const backgroundColor = theme.colors.variant.lightest[bsStyle];
  const borderColor = theme.colors.variant.light[bsStyle];
  return css`
    /** NOTE: Deprecated & should be removed in 4.0 */
    border-color: ${borderColor};
    background: ${theme.colors.table.row.background};

    & > .panel-heading {
      color: ${theme.utils.contrastingColor(backgroundColor)};
      background-color: ${backgroundColor};
      border-color: ${borderColor};

      > .panel-title,
      > .panel-title > * {
        font-size: ${theme.fonts.size.large};
      }

      + .panel-collapse > .panel-body {
        border-top-color: ${borderColor};
      }

      .badge {
        color: ${backgroundColor};
        background-color: ${theme.colors.variant[bsStyle]};
      }
    }

    & > .panel-footer {
      + .panel-collapse > .panel-body {
        border-bottom-color: ${borderColor};
      }
    }
  `;
});
const DeprecatedStyledPanel = styled(BootstrapPanel)(
  ({ theme }) => css`
    /** NOTE: Deprecated & should be removed in 4.0 */
    background-color: ${theme.utils.colorLevel(theme.colors.global.background, -4)};

    .panel-footer {
      background-color: ${theme.colors.gray[90]};
      border-top-color: ${theme.colors.gray[80]};
    }

    .panel-group {
      .panel-heading {
        + .panel-collapse > .panel-body,
        + .panel-collapse > .list-group {
          border-top-color: ${theme.colors.gray[90]};
        }
      }

      .panel-footer {
        + .panel-collapse .panel-body {
          border-bottom-color: ${theme.colors.gray[90]};
        }
      }
    }

    ${deprecatedVariantStyles}
  `
);
const Panel = ({
  title = void 0,
  children = void 0,
  collapsible = false,
  defaultExpanded = null,
  expanded = null,
  footer = void 0,
  header = void 0,
  onToggle = () => {
  },
  ...props
}) => {
  const [isExpanded, setIsExpanded] = useState(null);
  const didRender = useRef(false);
  useEffect(() => {
    setIsExpanded(
      (prevIsExpanded) => defaultExpanded && expanded || !defaultExpanded && expanded || defaultExpanded && prevIsExpanded === expanded
    );
  }, [expanded, defaultExpanded]);
  useEffect(() => {
    didRender.current = true;
  }, []);
  const handleToggle = (nextIsExpanded) => {
    setIsExpanded(nextIsExpanded);
    onToggle(nextIsExpanded);
  };
  const hasDeprecatedChildren = typeof children === "string" || Array.isArray(children) && typeof children[0] === "string";
  if (header || footer || title || collapsible || hasDeprecatedChildren) {
    if (!didRender.current) {
      deprecationNotice(
        "You have used a deprecated `Panel` prop, please check the documentation to use the latest `Panel`."
      );
    }
    return (
      /* NOTE: this exists as a deprecated render for older Panel instances */
      /* @__PURE__ */ React.createElement(DeprecatedStyledPanel, { expanded: isExpanded, onToggle: handleToggle, ...props }, (header || title) && /* @__PURE__ */ React.createElement(PanelHeading, null, header, title && /* @__PURE__ */ React.createElement(BootstrapPanel.Title, { toggle: collapsible }, title)), /* @__PURE__ */ React.createElement(DeprecatedStyledPanel.Body, { collapsible }, children), footer && /* @__PURE__ */ React.createElement(DeprecatedStyledPanel.Footer, null, footer))
    );
  }
  return /* @__PURE__ */ React.createElement(StyledPanel, { expanded: isExpanded, onToggle: handleToggle, defaultExpanded, ...props }, children);
};
Panel.Body = BootstrapPanel.Body;
Panel.Collapse = BootstrapPanel.Collapse;
Panel.Footer = PanelFooter;
Panel.Heading = PanelHeading;
Panel.Title = BootstrapPanel.Title;
Panel.Toggle = BootstrapPanel.Toggle;
export default Panel;
